import React, { useCallback, useState, useEffect } from "react";
import {
  BaseExpenseFieldsProps,
  ExpenseEntry,
  ExpenseFormData,
  GENERIC_EXPENSE_CATEGORIES,
  GenericExpenseCategory,
} from "./types";
import { Field, FormikErrors, useFormikContext } from "formik";
import {
  Box,
  Button,
  FormControlLabel,
  TextField,
  Typography,
  Grid,
  MenuItem,
  Checkbox,
} from "@mui/material";
import {
  commonDateFieldProps,
  commonTextFieldProps,
  smallButtonProp,
} from "../../../utils/styles";
import { formatInputCurrency } from "../../../utils/utils";
import { vehicleExpenseApi } from "../apiService";

const OutsourcedOwnerExpenseFields = ({
  index,
  isProjectExpense = false,
  isTripExpense = false,
}: BaseExpenseFieldsProps) => {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<ExpenseFormData>();
  const expenseErrors = errors.expenses?.[index] as
    | FormikErrors<ExpenseEntry>
    | undefined;

  const fetchOutsourcedOwnerDetails = useCallback(async () => {
    const response = await vehicleExpenseApi.getOutsourcedOwnerDetails(
      values.vehicleId || ""
    );
    if (response) {
      const owner = response?.outsourcedOwner[0];
      setFieldValue(`expenses.${index}.referenceId`, owner?.id);
      setFieldValue(`expenses.${index}.paidTo`, owner?.primaryContactName);
      setFieldValue(
        `expenses.${index}.contactNumber`,
        owner?.primaryContactNumber
      );
    }
  }, [values.vehicleId, index, setFieldValue]);

  useEffect(() => {
    if (values.vehicleId) {
      fetchOutsourcedOwnerDetails();
    }
  }, [fetchOutsourcedOwnerDetails, values.vehicleId]);

  const [addNotes, setAddNotes] = useState<boolean>(false);

  const handleMarkAsPaid = useCallback(
    (isPaid: boolean) => {
      if (isPaid) {
        setFieldValue(`expenses.${index}.payments`, [
          ...values.expenses[index].payments!,
          {
            type: "ADVANCE",
            mode: "CASH",
            date: values.expenses[index].expenseDate || "",
            location: values.expenses[index].location || "",
            receivedFrom: values.expenses[index].paidTo || "",
            remarks: "",
            status: "PAID",
            isEdited: false,
            isDeleted: false,
            isNew: true,
            amount: values.expenses[index].amount,
            masterExpenseId: values.expenses[index].id || null,
          },
        ]);
      } else {
        setFieldValue(`expenses.${index}.payments`, []);
      }
    },
    [index, setFieldValue, values.expenses]
  );

  return (
    <Grid container item xs={9} spacing={1}>
      <Grid item xs={12}>
        <Typography
          variant="subtitle2"
          gutterBottom
          sx={{ fontSize: "0.8rem" }}
        >
          Owner : {values.expenses[index].paidTo}{" "}
          {values.expenses[index].contactNumber && (
            <>({values.expenses[index].contactNumber})</>
          )}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Date"
          type="date"
          name={`expenses.${index}.expenseDate`}
          fullWidth
          required
          {...commonDateFieldProps}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field name={`expenses.${index}.amount`}>
          {({ field, meta }: any) => (
            <TextField
              {...field}
              {...commonTextFieldProps}
              label="Amount"
              fullWidth
              required
              error={meta.touched && Boolean(meta.error)}
              helperText={meta.touched && meta.error}
              value={field.value ? formatInputCurrency(field.value) : ""}
              onChange={(e) => {
                const value = e.target.value.replace(/,/g, "");
                if (value === "" || /^\d*\.?\d*$/.test(value)) {
                  field.onChange({
                    target: {
                      name: field.name,
                      value: value,
                    },
                  });
                  setFieldValue(`expenses.${index}.isEdited`, true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <Typography
                    component="span"
                    sx={{ mr: 0.5, fontSize: "0.75rem" }}
                  >
                    ₹
                  </Typography>
                ),
              }}
              inputProps={{
                inputMode: "decimal",
              }}
            />
          )}
        </Field>
      </Grid>

      <Grid item xs={12} sm={3}>
        <Field
          as={TextField}
          label="Location"
          name={`expenses.${index}.location`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setFieldValue(`expenses.${index}.location`, e.target.value);
            setFieldValue(`expenses.${index}.isEdited`, true);
          }}
          fullWidth
          placeholder="Enter location"
          {...commonTextFieldProps}
        />
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="outlined"
          onClick={() => setAddNotes(!addNotes)}
          size={smallButtonProp.size}
          sx={{
            ...smallButtonProp.sx,
            color: "grey",
            borderColor: "grey.400",
            "&:hover": {
              borderColor: "grey.400",
              color: "grey",
            },
          }}
        >
          <Typography variant="body2" sx={{ fontSize: "0.65rem" }}>
            {addNotes ? "Hide Notes" : "Add Notes"}
          </Typography>
        </Button>
        {Number(values.expenses[index].amount) > 0 &&
          values.expenses[index].payments?.length === 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    values.expenses[index].payments?.length! &&
                    values.expenses[index].payments?.length! > 0
                      ? true
                      : false
                  }
                  onChange={(e) => {
                    handleMarkAsPaid(e.target.checked);
                  }}
                  sx={{
                    "& .MuiSvgIcon-root": { fontSize: "0.7rem" },
                  }}
                />
              }
              label="Mark as Paid"
              sx={{
                p: 0,
                pr: 1,
                ml: 0.75,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.7rem",
                  color: "primary.main",
                },
              }}
            />
          )}
      </Grid>
      {addNotes && (
        <Grid item xs={12}>
          <Field
            as={TextField}
            label="Notes"
            name={`expenses.${index}.notes`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(`expenses.${index}.notes`, e.target.value);
              setFieldValue(`expenses.${index}.isEdited`, true);
            }}
            fullWidth
            multiline
            rows={2}
            placeholder="Any additional notes about this expense..."
            {...commonTextFieldProps}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default OutsourcedOwnerExpenseFields;
